import { Landmark, Loader2 } from "lucide-react";
import styles from "../../styles/applicationDetail.module.css";
import type {
  BankLinkingEditSectionProps,
  BankLinkingViewSectionProps,
} from "../../types/tabs";

/**
 * Edit Bank Linking — matches Angular `resident-application-add` employmentinfo:
 * - "Link My Bank" when no accounts OR first account is `reconnect_required`
 * - "Your Linked Bank:" list when any ninja accounts exist
 * - No View Logs / Link Another (those are React-only extras)
 */
export function BankLinkingEditSection({
  ninjaAccounts,
  ninjaConfig,
  isBankLinkingLoading,
  isOpeningNinjaBankLink,
  onLinkBank,
}: BankLinkingEditSectionProps) {
  const showLinkMyBank =
    Boolean(ninjaConfig) &&
    (ninjaAccounts.length === 0 ||
      ninjaAccounts[0]?.ibv_report_status === "reconnect_required");
  const linkingBusy = isBankLinkingLoading || isOpeningNinjaBankLink;

  return (
    <div
      className={styles.incomeNestedSection}
      data-testid="application-income-tab-section-bank-linking"
    >
      <h3 className={styles.incomeSubsectionTitle}>
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <Landmark className="h-4 w-4" />
        </span>
        Bank Linking
      </h3>

      {showLinkMyBank ? (
        <div className="mb-2">
          <button
            type="button"
            data-testid="application-income-tab-link-bank-button"
            onClick={() => void onLinkBank()}
            disabled={linkingBusy}
            aria-busy={linkingBusy}
            className="inline-flex w-fit items-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {linkingBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : null}
            Link My Bank
          </button>
          {ninjaConfig?.NINJA_REQUIRED === "YES" ? (
            <div className="mt-1">
              <small className="text-xs italic text-red-500">*required</small>
            </div>
          ) : null}
          <p className={`${styles.incomeEditNote} mt-2`}>
            <span>
              Linking your bank is the best way for us to verify your income and
              identity, and it provides us with the most thorough and accurate
              information. This connection is a one-time verification, and for
              your protection, we never see or store your bank login details.
            </span>
          </p>
        </div>
      ) : null}

      {ninjaAccounts.length > 0 ? (
        <div className="mb-2" data-testid="application-income-tab-linked-banks">
          <p className="m-0 mb-2 text-[14px] font-medium text-[#1597FF]">
            Your Linked Bank:
          </p>
          {ninjaAccounts.map((ninjaAccount, attachIndex) => {
            const status = ninjaAccount.ibv_report_status || "";
            const showBankDetail = status !== "initiated";
            return (
              <div
                key={ninjaAccount.pli_id ?? attachIndex}
                className="mb-2 flex items-start gap-3"
              >
                {showBankDetail ? (
                  <>
                    <div className="mt-0.5 text-slate-700">
                      <Landmark className="h-8 w-8" aria-hidden />
                    </div>
                    <p className="m-0 text-[14px] text-slate-800">
                      <strong className="text-[15px]">{ninjaAccount.bank_name}</strong>
                      <br />
                      {(ninjaAccount as { account_name?: string }).account_name ||
                        ""}
                      {(ninjaAccount as { account_name?: string }).account_name
                        ? " : "
                        : ""}
                      {ninjaAccount.account_type}
                    </p>
                  </>
                ) : (
                  <p className="m-0 text-[14px] text-slate-800">Processing Result</p>
                )}
              </div>
            );
          })}
          <p className={`${styles.incomeEditNote} mt-2`}>
            <span>
              Thank you for linking your bank with us! This helps us speed things
              along for you and your data is secure. Please be aware that we still
              may ask for additional proof of current employment.
            </span>
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Read-only Bank Linking — matches Angular `application-details` #employmentDataView. */
export function BankLinkingViewSection({
  ninjaAccounts,
  showViewLogsButton = false,
  viewLogsLoading = false,
  onViewLogs,
}: BankLinkingViewSectionProps) {
  return (
    <div
      className={styles.section}
      data-testid="application-income-tab-view-section-bank-linking"
    >
      <div className={`${styles.sectionHeader} ${styles.bankLinkingSectionHeader}`}>
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <Landmark className="h-4 w-4" />
          </span>
          <span className={styles.sectionTitleText}>Bank Linking</span>
        </h2>
        {showViewLogsButton ? (
          <button
            type="button"
            data-testid="application-income-tab-view-bank-linking-logs-button"
            onClick={() => onViewLogs?.()}
            disabled={viewLogsLoading}
            aria-busy={viewLogsLoading}
            className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {viewLogsLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : null}
            View Logs
          </button>
        ) : null}
      </div>
      {ninjaAccounts.length > 0 && (
        <>
          {ninjaAccounts.map((ninjaAccount, attachIndex) => {
            const status = ninjaAccount.ibv_report_status || "";
            const showBankDetail = status !== "initiated";
            const showFetchUi = [
              "initiated",
              "ibv_data_acquire_failed",
            ].includes(status);

            return (
              <div
                key={ninjaAccount.pli_id ?? attachIndex}
                className={styles.infoRow}
                style={{ marginBottom: "1rem", alignItems: "flex-start" }}
              >
                {showBankDetail && (
                  <div className={styles.infoColumn}>
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-slate-700">
                        <Landmark className="h-8 w-8" aria-hidden />
                      </div>
                      <p className="m-0">
                        <strong>{ninjaAccount.bank_name}</strong>
                        <br />
                        {(ninjaAccount as { account_name?: string })
                          .account_name || ""}{" "}
                        {ninjaAccount.account_type}
                      </p>
                    </div>
                  </div>
                )}
                {showFetchUi && (
                  <div className={styles.infoColumn}>
                    <h6>Processing Result</h6>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
